import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import * as d3 from "d3";
import {
  Grid,
  Paper,
  Box,
  Typography,
  SelectChangeEvent,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import { MIBScopeData } from "./data/MIBScopeData";
import { Constants } from "constants/constant";
import { downloadChartImage } from "utilities/downloadChartImage";

const wrapAxisText = (
  texts: d3.Selection<SVGTextElement, string, SVGGElement, unknown>,
  bandWidth: number
) => {
  texts.each(function () {
    const textEl = d3.select(this);
    const rawWords = textEl.text().split(/[\s/]+/).filter(Boolean);
    const words: string[] = [];

    rawWords.forEach((w) => {
      if (w.length > 20) {
        for (let i = 0; i < w.length; i += 10) {
          words.push(w.slice(i, i + 10));
        }
      } else {
        words.push(w);
      }
    });

    words.reverse();
    let word: string | undefined;
    let line: string[] = [];
    let lineNumber = 0;
    const lineHeight = 1.2;
    const y = textEl.attr("y");
    const dy = parseFloat(textEl.attr("dy") ?? "0");

    let tspan = textEl
      .text(null)
      .append("tspan")
      .attr("x", 0)
      .attr("y", y)
      .attr("dy", `${dy}em`);

    while ((word = words.pop())) {
      line.push(word);
      tspan.text(line.join(" "));
      const node = tspan.node() as SVGTSpanElement | null;

      if (node && node.getComputedTextLength() > bandWidth - 4) {
        line.pop();
        tspan.text(line.join(" "));
        line = [word];
        tspan = textEl
          .append("tspan")
          .attr("x", 0)
          .attr("y", y)
          .attr("dy", `${++lineNumber * lineHeight + dy}em`)
          .text(word);
      }
    }

    if (line.length > 0) {
      tspan.text(line.join(" "));
    }
  });
};

const defaultGroupByOptions = [
  { label: "Location", value: "Location" },
  { label: "Measure Type", value: "Type" },
  { label: "Measure Category", value: "Category" },
  { label: "Individual Measures", value: "Individual" },
];

const DENSE_BAR_THRESHOLD = 12;

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

type MeasureImpactsbyScopeCurveProps = {
  data?: MIBScopeData[];
  currency: string;
  groupBy?: string;
  groupByOptions?: Array<{ label: string; value: string }>;
  fitContainerWidth?: boolean;
  isExportCapture?: boolean;
  onGroupByChange?: (value: string) => void;
  fetchData?: (groupBy: string) => Promise<MIBScopeData[]>;
};

export type MeasureImpactsbyScopeRef = {
  downloadChart: (format: "png" | "jpeg") => void;
  _testSvgRef?: React.RefObject<SVGSVGElement | null>;
};

interface LegendItem {
  color?: string;
  label: string;
  value: number;
}

type SafeMeasureRow = {
  measureName: string;
  emissionReduction: number;
};

type SafeOrganizationRow = {
  organizationName: string;
  emissionReduction: number;
};

type SafeScopeByEmissionRow = {
  scopeType: string;
  emissionReduction: number;
  percentage?: number;
};

type SafeMIBScopeData = MIBScopeData & {
  label: string;
  value: number;
  measures?: SafeMeasureRow[];
  organizations?: SafeOrganizationRow[];
  scopeByEmissions?: SafeScopeByEmissionRow[];
  barType?: string;
  color?: string;
};

const toSafeNumber = (value: unknown, fallback = 0): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const toSafeString = (value: unknown, fallback = "Unknown"): string => {
  return typeof value === "string" && value.trim() !== "" ? value : fallback;
};

const formatTwoDecimalValue = (value: number): string =>
  toSafeNumber(value, 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getScopeShortLabel = (scopeType: string): string => {
  const normalized = scopeType.trim().toLowerCase();
  const matchedScope = normalized.match(/^scope\s*(\d+)$/i);
  if (matchedScope?.[1]) {
    return `S${matchedScope[1]}`;
  }
  return scopeType;
};

const isResidualEmissionLabel = (label: string): boolean =>
  /residual\s+emission(s)?/i.test(toSafeString(label, "").trim());

const sanitizeMIBScopeData = (input?: MIBScopeData[]): SafeMIBScopeData[] => {
  if (!Array.isArray(input)) {
    return [];
  }

  return input
    .filter((item) => item != null && typeof item === "object")
    .map((item: any) => ({
      ...item,
      label: toSafeString(item?.label, "Unknown"),
      value: toSafeNumber(item?.value, 0),
      color: typeof item?.color === "string" ? item.color : undefined,
      barType: typeof item?.barType === "string" ? item.barType : undefined,
      measures: Array.isArray(item?.measures)
        ? item.measures
            .filter((m: any) => m != null && typeof m === "object")
            .map((m: any) => ({
              measureName: toSafeString(m?.measureName, "Unknown"),
              emissionReduction: toSafeNumber(m?.emissionReduction, 0),
            }))
        : [],
      organizations: Array.isArray(item?.organizations)
        ? item.organizations
            .filter((o: any) => o != null && typeof o === "object")
            .map((o: any) => ({
              organizationName: toSafeString(o?.organizationName, "Unknown"),
              emissionReduction: toSafeNumber(o?.emissionReduction, 0),
            }))
        : [],
      scopeByEmissions: Array.isArray(item?.scopeByEmissions)
        ? item.scopeByEmissions
            .filter((s: any) => s != null && typeof s === "object")
            .map((s: any) => ({
              scopeType: toSafeString(s?.scopeType, "Unknown Scope"),
              emissionReduction: toSafeNumber(s?.emissionReduction, 0),
              percentage:
                typeof s?.percentage === "number" && Number.isFinite(s.percentage)
                  ? s.percentage
                  : undefined,
            }))
        : []
    }));
};

const MeasureImpactsbyScope = forwardRef<
  MeasureImpactsbyScopeRef,
  MeasureImpactsbyScopeCurveProps
>(
  (
    {
      data,
      currency,
      groupBy = "Type",
      groupByOptions = defaultGroupByOptions,
      fitContainerWidth = false,
      isExportCapture = false,
      onGroupByChange,
      fetchData,
    },
    ref
  ) => {
    const svgRef = useRef<SVGSVGElement | null>(null);
    const svgDialogRef = useRef<SVGSVGElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const tooltipDialogRef = useRef<HTMLDivElement | null>(null);
    const UNIT_TCO2E = Constants.UNIT_TCO2E;
    const [showLabels, setShowLabels] = useState(true);
    const [hasManualLabelPreference, setHasManualLabelPreference] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [dialogGroupBy, setDialogGroupBy] = useState(groupBy);
    const [dialogLocalData, setDialogLocalData] = useState<MIBScopeData[]>([]);
    const [dialogLoading, setDialogLoading] = useState(false);
    const userChangedDialogGroupByRef = useRef(false);
    const containerBoxRef = useRef<HTMLDivElement | null>(null);

    const EXCLUDED_LABELS = new Set([
      "Residual emissions",
      "Residual emission",
      "Historical Emissions",
      "Growth",
    ]);

    const safeMainData = sanitizeMIBScopeData(data);
    const safeDialogData = sanitizeMIBScopeData(dialogLocalData);

    const hasDialogData = isFullscreen && safeDialogData.length > 0;
    const dataLength = safeMainData.length;
    const activeDataLength = hasDialogData ? safeDialogData.length : dataLength;

    const handleGroupByChange = (event: SelectChangeEvent<string>) => {
      const value = event.target.value;
      if (onGroupByChange) {
        onGroupByChange(value);
      }
    };

    const handleDialogGroupByChange = (event: SelectChangeEvent<string>) => {
      userChangedDialogGroupByRef.current = true;
      setDialogGroupBy(event.target.value);
    };

    const getLegendColor = (item: LegendItem): string => {
      if (item.color != null) return item.color;
      if (isResidualEmissionLabel(item.label)) return "#A9A9A9";
      if (item.label === "Historical Emissions") return "#5e4bea";
      if (item.label === "Growth") return "#A020F0";
      if (item.value > 0) return "#2E90FA";
      return "#17B26A";
    };

    const getResidualScopeColor = (scopeType: string): string => {
      const normalized = scopeType.trim().toLowerCase();
      if (normalized === "scope 1") return "#4472c4";
      if (normalized === "scope 2") return "#70ad47";
      if (normalized === "scope 3") return "#ffc000";
      return "#667085";
    };

    const buildChart = (
      targetSvgRef: React.RefObject<SVGSVGElement | null>,
      targetTooltipRef: React.RefObject<HTMLDivElement | null>,
      fullscreen: boolean,
      activeGroupBy: string,
      sourceData?: MIBScopeData[]
    ) => {
      if (!targetSvgRef.current) {
        return;
      }

      const safeSourceData = sanitizeMIBScopeData(sourceData ?? data ?? []);
      const rootSvg = d3.select(targetSvgRef.current);
      rootSvg.selectAll("*").remove();

      const exportFitMode = isExportCapture && !fullscreen;

      if (safeSourceData.length === 0) {
        rootSvg
          .attr("width", 1100)
          .attr("height", 260)
          .append("text")
          .attr("x", 180)
          .attr("y", 120)
          .attr("fill", "#64748b")
          .style("font-size", "16px")
          .text("No valid data available for Decarbonization Waterfall.");
        return;
      }

      const isDense = safeSourceData.length > DENSE_BAR_THRESHOLD;
      const hasAnyScopeBreakdown = safeSourceData.some(
        (item) => (item.scopeByEmissions?.length ?? 0) > 0
      );
      const margin = {
        top: 30,
        right: hasAnyScopeBreakdown ? (exportFitMode ? 170 : 190) : 30,
        bottom: isDense ? (exportFitMode ? 170 : 140) : 120,
        left: exportFitMode ? 90 : 100,
      };

      if (!fullscreen && targetSvgRef.current) {
        targetSvgRef.current.removeAttribute("width");
        targetSvgRef.current.removeAttribute("height");
      }

      const availableHeight = fullscreen
        ? Math.min(window.innerHeight - 220, 520)
        : 500;
      const height = availableHeight - margin.top - margin.bottom;

      const hasExplicitWaterfallBars = safeSourceData.some(
        (d) => d.barType === "start" || d.barType === "end"
      );

      const baseline = 0;
      let chartData: Array<
        SafeMIBScopeData & { start: number; end: number; baseline: number }
      > = [];

      const residualItem =
        safeSourceData.find((item) => isResidualEmissionLabel(item.label)) ??
        safeSourceData.find((item) => item.barType === "end");
      const scopeBreakdownSource =
        Array.isArray(residualItem?.scopeByEmissions) &&
        (residualItem?.scopeByEmissions?.length ?? 0) > 0
          ? residualItem?.scopeByEmissions ?? []
          : safeSourceData.flatMap((item) => item.scopeByEmissions ?? []);

      const residualScopeBreakdownMap = new Map<string, { emissionReduction: number; percentage: number }>();
      scopeBreakdownSource.forEach((item) => {
        const scopeType = toSafeString(item?.scopeType, "");
        if (!scopeType) return;
        const normalizedScope = scopeType.trim().toLowerCase();
        const existing = residualScopeBreakdownMap.get(normalizedScope) ?? { emissionReduction: 0, percentage: 0 };
        residualScopeBreakdownMap.set(
          normalizedScope,
          {
            emissionReduction: existing.emissionReduction + toSafeNumber(item?.emissionReduction, 0),
            percentage: toSafeNumber(item?.percentage, existing.percentage),
          }
        );
      });

      const residualScopeBreakdown = Array.from(residualScopeBreakdownMap.entries())
        .map(([normalizedScope, data]) => ({
          scopeType: normalizedScope
            .split(" ")
            .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
            .join(" "),
          emissionReduction: data.emissionReduction,
          percentage: data.percentage,
        }))
        .filter((item) => item.scopeType !== "");

      if (hasExplicitWaterfallBars) {
        let cumulative = 0;
        chartData = safeSourceData.map((d) => {
          if (d.barType === "start") {
            cumulative = d.value;
            return { ...d, start: 0, end: d.value, baseline };
          }

          if (d.barType === "end") {
            return { ...d, start: 0, end: d.value, baseline };
          }

          if (d.barType === "growth") {
            const start = cumulative;
            cumulative += Math.abs(d.value);
            const end = cumulative;
            return { ...d, start, end, baseline };
          }

          const start = cumulative;
          cumulative -= Math.abs(d.value);
          const end = cumulative;
          return { ...d, start, end, baseline };
        });
      } else {
        let cumulative = 0;
        chartData = safeSourceData.map((d) => {
          if (isResidualEmissionLabel(d.label)) {
            return { ...d, start: 0, end: d.value, baseline, barType: "end" };
          }

          const start = cumulative;
          if (d.value >= 0) {
            cumulative += d.value;
          } else {
            cumulative -= Math.abs(d.value);
          }
          const end = cumulative;
          return { ...d, start, end, baseline };
        });
      }

      if (chartData.length === 0) {
        rootSvg
          .attr("width", 1100)
          .attr("height", 260)
          .append("text")
          .attr("x", 180)
          .attr("y", 120)
          .attr("fill", "#64748b")
          .style("font-size", "16px")
          .text("No chart data available.");
        return;
      }

      const minBarWidth = exportFitMode ? 72 : 90;
      const defaultContainerWidth = 1100;

      const measuredContainerWidth =
        !fullscreen && containerBoxRef.current
          ? containerBoxRef.current.offsetWidth
          : defaultContainerWidth;

      const containerWidth =
        measuredContainerWidth > 0 ? measuredContainerWidth : defaultContainerWidth;

      const availableWidth = fullscreen
        ? window.innerWidth - margin.left - margin.right - 80
        : containerWidth - margin.left - margin.right;

      const contentWidth = chartData.length * minBarWidth;
      let width = availableWidth;

      if (fullscreen || !fitContainerWidth || (isDense && !exportFitMode)) {
        width = Math.max(availableWidth, contentWidth);
      }

      if (exportFitMode) {
        width = availableWidth;
      }

      const totalSvgWidth = width + margin.left + margin.right;
      const totalSvgHeight = height + margin.top + margin.bottom;

      const keyedChartData = chartData.map((d, index) => ({
        ...d,
        xKey: `${d.label}__${index}`,
      }));

      const minDomain = d3.min(keyedChartData, (d) => Math.min(d.start, d.end));
      const maxDomain = d3.max(keyedChartData, (d) => Math.max(d.start, d.end));

      if (!Number.isFinite(minDomain) || !Number.isFinite(maxDomain)) {
        rootSvg
          .attr("width", totalSvgWidth)
          .attr("height", 260)
          .append("text")
          .attr("x", 180)
          .attr("y", 120)
          .attr("fill", "#64748b")
          .style("font-size", "16px")
          .text("Invalid chart data received.");
        return;
      }

      rootSvg.attr("width", totalSvgWidth).attr("height", totalSvgHeight);

      const svg = rootSvg
        .append("g")
        .attr("transform", `translate(${margin.left + 10},${margin.top})`);

      const barOffsetX = exportFitMode ? 24 : 40;
      const axisOffsetX = exportFitMode ? 14 : 30;
      const yAxisOffsetX = exportFitMode ? 34 : 50;
      const xRangeWidth = exportFitMode ? Math.max(width - barOffsetX - 6, 1) : width;

      const xLabelByKey = new Map(
        keyedChartData.map((d) => [d.xKey, d.label] as const)
      );

      const x = d3
        .scaleBand<string>()
        .domain(keyedChartData.map((d) => d.xKey))
        .range([0, xRangeWidth])
        .padding(0.3);

      const yDomainMin = Math.min(Number(minDomain), 0);
      const yDomainMax = Math.max(Number(maxDomain), 0);

      const y = d3
        .scaleLinear()
        .domain([yDomainMin, yDomainMax])
        .nice()
        .range([height, 10]);

      const residualBarData = keyedChartData.find((item) =>
        isResidualEmissionLabel(item.label)
      );

      const hasResidualScopeBreakdown =
        residualBarData != null && residualScopeBreakdown.length > 0;

      const regularBarData = hasResidualScopeBreakdown
        ? keyedChartData.filter((item) => item !== residualBarData)
        : keyedChartData;

      const xAxisGroup = svg
        .append("g")
        .attr("transform", `translate(${axisOffsetX}, ${height})`)
        .call(
          d3
            .axisBottom(x)
            .tickFormat((key) => xLabelByKey.get(String(key)) ?? String(key))
        );

      xAxisGroup
        .selectAll<SVGTextElement, string>("text")
        .style("text-anchor", "middle")
        .attr("dx", "0")
        .attr("dy", "0.8em")
        .style("font-size", exportFitMode && isDense ? "10px" : "11px")
        .style("fill", "#7A7A9D")
        .call(wrapAxisText, x.bandwidth());

      svg
        .append("g")
        .call(d3.axisLeft(y))
        .attr("transform", `translate(${yAxisOffsetX}, 0)`)
        .selectAll("text")
        .style("font-size", "12px")
        .style("fill", "#7A7A9D");

      svg
        .append("text")
        .attr("transform", "rotate(-90)")
        .attr("x", 0 - height / 2)
        .attr("y", 0 - margin.left + 10)
        .attr("dy", "1em")
        .style("text-anchor", "middle")
        .text(`Annual Emission Reduction (${UNIT_TCO2E})`)
        .style("font-size", "14px")
        .style("fill", "#425466");

      function showTooltip(
        event: MouseEvent,
        d: (typeof keyedChartData)[number],
        tooltipTargetRef: React.RefObject<HTMLDivElement | null>,
        grpBy: string
      ) {
        if (EXCLUDED_LABELS.has(d.label)) return;

        const tooltip = tooltipTargetRef.current;
        if (!tooltip) return;

        const supportsBreakdown =
          grpBy === "Organization" ||
          grpBy === "Location" ||
          grpBy === "Type" ||
          grpBy === "Category" ||
          grpBy === "Individual";

        if (!supportsBreakdown) return;

        const formatReductionValue = (value: number | undefined) => {
          const numericValue = value ?? 0;
          if (Object.is(numericValue, 0) || Object.is(numericValue, -0)) {
            return "0";
          }
          return `-${Math.abs(numericValue).toLocaleString()}`;
        };

        const safeLabel = escapeHtml(toSafeString(d.label, "Unknown"));
        const totalReduction = toSafeNumber(d.value, 0).toLocaleString();

        const measureRows = (Array.isArray(d.measures) ? d.measures : [])
          .map(
            (m) => `<tr>
                <td style="padding:2px 8px 2px 0;color:#27272E;overflow-wrap:anywhere;word-break:break-word;">${escapeHtml(
                  toSafeString(m.measureName, "Unknown")
                )}</td>
                <td style="padding:2px 0;color:#17B26A;text-align:right;">${formatReductionValue(
                  toSafeNumber(m.emissionReduction, 0)
                )}</td>
            </tr>`
          )
          .join("");

        if (measureRows) {
          tooltip.innerHTML = `
              <div style="font-weight:600;margin-bottom:4px;color:#05004E;">${safeLabel}</div>
              <div style="font-size:12px;color:#475467;margin-bottom:6px;">Total reduction: ${totalReduction}</div>
              <table style="border-collapse:collapse;font-size:12px;width:100%;table-layout:fixed;">
                  <thead><tr>
                      <th style="padding:2px 8px 4px 0;color:#7A7A9D;text-align:left;overflow-wrap:anywhere;">Measure</th>
                      <th style="padding:2px 0 4px;color:#7A7A9D;text-align:right;">Emission Reduction</th>
                  </tr></thead>
                  <tbody>${measureRows}</tbody>
              </table>`;
          tooltip.style.display = "block";
          tooltip.style.left = `${event.offsetX + 16}px`;
          tooltip.style.top = `${event.offsetY + 16}px`;
          return;
        }

        const organizationRows = (Array.isArray(d.organizations)
          ? d.organizations
          : []
        )
          .map(
            (org) => `<tr>
                <td style="padding:2px 8px 2px 0;color:#27272E;overflow-wrap:anywhere;word-break:break-word;">${escapeHtml(
                  toSafeString(org.organizationName, "Unknown")
                )}</td>
                <td style="padding:2px 0;color:#17B26A;text-align:right;">${formatReductionValue(
                  toSafeNumber(org.emissionReduction, 0)
                )}</td>
            </tr>`
          )
          .join("");

        if (!organizationRows) return;

        tooltip.innerHTML = `
            <div style="font-weight:600;margin-bottom:4px;color:#05004E;">${safeLabel}</div>
            <div style="font-size:12px;color:#475467;margin-bottom:6px;">Total reduction: ${totalReduction}</div>
            <table style="border-collapse:collapse;font-size:12px;width:100%;table-layout:fixed;">
                <thead><tr>
                    <th style="padding:2px 8px 4px 0;color:#7A7A9D;text-align:left;overflow-wrap:anywhere;">Organization</th>
                    <th style="padding:2px 0 4px;color:#7A7A9D;text-align:right;">Emission Reduction</th>
                </tr></thead>
                <tbody>${organizationRows}</tbody>
            </table>`;

        tooltip.style.display = "block";
        tooltip.style.left = `${event.offsetX + 16}px`;
        tooltip.style.top = `${event.offsetY + 16}px`;
      }

      function moveTooltip(
        event: MouseEvent,
        tooltipTargetRef: React.RefObject<HTMLDivElement | null>
      ) {
        const tooltip = tooltipTargetRef.current;
        if (!tooltip) return;
        tooltip.style.left = `${event.offsetX + 16}px`;
        tooltip.style.top = `${event.offsetY + 16}px`;
      }

      function hideTooltip(
        tooltipTargetRef: React.RefObject<HTMLDivElement | null>
      ) {
        const tooltip = tooltipTargetRef.current;
        if (tooltip) tooltip.style.display = "none";
      }

      function showResidualBreakdownTooltip(
        event: MouseEvent,
        scopeBreakdown: Array<{ scopeType: string; emissionReduction: number; percentage?: number }>,
        tooltipTargetRef: React.RefObject<HTMLDivElement | null>
      ) {
        const tooltip = tooltipTargetRef.current;
        if (!tooltip) return;

        const lines = scopeBreakdown
          .map(
            (item) => `<div style="padding:2px 0;color:#101828;font-size:12px;">${escapeHtml(
              item.scopeType
            )} - <span style="font-weight:600;">${formatTwoDecimalValue(
              item.emissionReduction
            )}${item.percentage === 0 || item.percentage ? ` (${toSafeNumber(item.percentage, 0).toFixed(1)}%)` : ""}</span></div>`
          )
          .join("");

        if (!lines) return;

        tooltip.innerHTML = `
            <div style="font-weight:600;margin-bottom:6px;color:#05004E;">Residual emissions</div>
            <div style="color:#7A7A9D;font-size:11px;margin-bottom:2px;">Values in ${escapeHtml(
              UNIT_TCO2E
            )}</div>
            ${lines}`;

        tooltip.style.display = "block";
        tooltip.style.left = `${event.offsetX + 16}px`;
        tooltip.style.top = `${event.offsetY + 16}px`;
      }

      svg
        .selectAll(".bar")
        .data(regularBarData)
        .enter()
        .append("rect")
        .attr("class", "bar")
        .attr("x", (d) => (x(d.xKey) ?? 0))
        .attr("y", (d) => y(Math.max(d.start, d.end)))
        .attr("height", (d) => Math.abs(y(d.start) - y(d.end)))
        .attr("width", x.bandwidth())
        .attr("transform", `translate(${barOffsetX}, 0)`)
        .attr("fill", (d) => {
          if (d.color) return d.color;
          if (isResidualEmissionLabel(d.label)) return "#A9A9A9";
          if (d.label === "Historical Emissions") return "#5e4bea";
          if (d.label === "Growth") return "#A020F0";
          if (d.value > 0) return "#2E90FA";
          return "#17B26A";
        })
        .on("mouseover", function (event, d) {
          showTooltip(event, d, targetTooltipRef, activeGroupBy);
        })
        .on("mousemove", function (event) {
          moveTooltip(event, targetTooltipRef);
        })
        .on("mouseout", function () {
          hideTooltip(targetTooltipRef);
        });

      if (hasResidualScopeBreakdown && residualBarData) {
        const orderedScopeLabels = ["scope 1", "scope 2", "scope 3"];
        const orderedScopeData = [
          ...orderedScopeLabels
            .map((scopeLabel) =>
              residualScopeBreakdown.find(
                (item) => item.scopeType.trim().toLowerCase() === scopeLabel
              )
            )
            .filter((item): item is typeof residualScopeBreakdown[0] => Boolean(item)),
          ...residualScopeBreakdown.filter(
            (item) =>
              !orderedScopeLabels.includes(item.scopeType.trim().toLowerCase())
          ),
        ];

        // Scope values can be negative; stacking them as-is drags the column below the
        // zero line, so only positive contributions are stacked and rescaled to fill
        // the residual bar.
        const residualBarValue = Math.max(
          toSafeNumber(residualBarData.value, 0),
          0
        );
        const positiveScopeTotal = orderedScopeData.reduce(
          (sum, item) => sum + Math.max(toSafeNumber(item.emissionReduction, 0), 0),
          0
        );
        const scopeScale =
          positiveScopeTotal > 0 && residualBarValue > 0
            ? residualBarValue / positiveScopeTotal
            : 1;

        let scopeRunningTotal = 0;
        const residualSegments = orderedScopeData.map((scopeItem) => {
          const rawValue = toSafeNumber(scopeItem.emissionReduction, 0);
          const segmentStart = scopeRunningTotal;
          scopeRunningTotal += Math.max(rawValue, 0) * scopeScale;
          const segmentEnd = scopeRunningTotal;
          return {
            scopeType: scopeItem.scopeType,
            value: rawValue,
            percentage: toSafeNumber(scopeItem.percentage, 0),
            start: segmentStart,
            end: segmentEnd,
            color: getResidualScopeColor(scopeItem.scopeType),
          };
        });

        // Tooltip mirrors what is rendered, so scopes with no visible segment are dropped.
        const visibleScopeData = orderedScopeData
          .filter((item) => toSafeNumber(item.emissionReduction, 0) > 0)
          .map((item) => ({
            scopeType: item.scopeType,
            emissionReduction: toSafeNumber(item.emissionReduction, 0),
            percentage: toSafeNumber(item.percentage, 0),
          }));

        const residualX = (x(residualBarData.xKey) ?? 0) + barOffsetX;
        const minInsideLabelHeight = 24;
        const outsideLabelGap = 16;
        const outsideLabelX = residualX + x.bandwidth() + 14;
        const outsideConnectorX = residualX + x.bandwidth() + 10;

        svg
          .selectAll(".residual-scope-segment")
          .data(residualSegments)
          .enter()
          .append("rect")
          .attr("class", "residual-scope-segment")
          .attr("x", residualX)
          .attr("y", (d) => y(Math.max(d.start, d.end)))
          .attr("height", (d) => Math.abs(y(d.start) - y(d.end)))
          .attr("width", x.bandwidth())
          .attr("fill", (d) => d.color)
          .attr("fill-opacity", 0.40)
          .on("mouseover", function (event) {
            showResidualBreakdownTooltip(
              event,
              visibleScopeData,
              targetTooltipRef
            );
          })
          .on("mousemove", function (event) {
            moveTooltip(event, targetTooltipRef);
          })
          .on("mouseout", function () {
            hideTooltip(targetTooltipRef);
          });
        const residualSegmentsWithLayout = residualSegments
          .filter((segment) => segment.value > 0)
          .map((segment) => {
            const topY = y(Math.max(segment.start, segment.end));
            const segmentHeight = Math.abs(y(segment.start) - y(segment.end));
            const centerY = topY + segmentHeight / 2;
            const showInside = segmentHeight >= minInsideLabelHeight;
            return {
              ...segment,
              topY,
              segmentHeight,
              centerY,
              showInside,
            };
          });

        const outsideLabelSegments = residualSegmentsWithLayout
          .sort((a, b) => a.centerY - b.centerY)
          .map((segment) => ({ ...segment, outsideY: segment.centerY }));

        const minOutsideY = 14;
        const maxOutsideY = height - 6;

        // Run multiple forward/backward passes to reach a stable non-overlapping layout
        for (let pass = 0; pass < 5; pass += 1) {
          for (let i = 1; i < outsideLabelSegments.length; i += 1) {
            outsideLabelSegments[i].outsideY = Math.max(
              outsideLabelSegments[i].outsideY,
              outsideLabelSegments[i - 1].outsideY + outsideLabelGap
            );
          }
          for (let i = outsideLabelSegments.length - 2; i >= 0; i -= 1) {
            outsideLabelSegments[i].outsideY = Math.min(
              outsideLabelSegments[i].outsideY,
              outsideLabelSegments[i + 1].outsideY - outsideLabelGap
            );
          }
        }

        // If labels overflow past maxOutsideY, shift all upward uniformly
        const lastSeg = outsideLabelSegments[outsideLabelSegments.length - 1];
        if (lastSeg && lastSeg.outsideY > maxOutsideY) {
          const overflow = lastSeg.outsideY - maxOutsideY;
          outsideLabelSegments.forEach((segment) => {
            segment.outsideY -= overflow;
          });
        }

        // Final clamp to ensure no label falls outside the visible area
        outsideLabelSegments.forEach((segment) => {
          segment.outsideY = Math.max(minOutsideY, Math.min(maxOutsideY, segment.outsideY));
        });

        svg
          .selectAll(".residual-scope-connector")
          .data(outsideLabelSegments)
          .enter()
          .append("line")
          .attr("class", "residual-scope-connector")
          .attr("x1", residualX + x.bandwidth())
          .attr("y1", (d) => d.centerY)
          .attr("x2", outsideConnectorX)
          .attr("y2", (d) => d.outsideY)
          .style("stroke", (d) => d.color)
          .style("stroke-width", "1")
          .style("display", "none");

        svg
          .selectAll(".residual-scope-label-outside")
          .data(outsideLabelSegments)
          .enter()
          .append("text")
          .attr("class", "residual-scope-label-outside")
          .attr("x", outsideLabelX)
          .attr("y", (d) => d.outsideY)
          .attr("text-anchor", "start")
          .attr("dominant-baseline", "middle")
          .style("font-size", "10px")
          .style("font-weight", "600")
          .style("fill", "#344054")
          .style("paint-order", "stroke")
          .style("stroke", "#FFFFFF")
          .style("stroke-width", "2px")
          .style("pointer-events", "none")
          .style("display", "none")
          .text((d) => {
            const baseText = getScopeShortLabel(d.scopeType);
            const scopeItem = orderedScopeData.find(
              (item) => item.scopeType === d.scopeType
            );
            const percentage = scopeItem?.percentage ?? toSafeNumber(d.percentage, 0);
            if (scopeItem?.percentage !== undefined) {
              return `${baseText} (${toSafeNumber(percentage, 0).toFixed(1)}%)`;
            }
            return baseText;
          });

        const residualTotal = residualSegments.reduce(
          (sum, segment) => sum + toSafeNumber(segment.value, 0),
          0
        );

        svg
          .append("text")
          .attr("class", "residual-total-label")
          .attr("x", residualX + x.bandwidth() / 2)
          .attr("y", y(Math.max(0, residualTotal)) - 6)
          .attr("text-anchor", "middle")
          .style("font-size", "10px")
          .style("fill", "#425466")
          .style("cursor", "default")
          .style("display", showLabels ? "block" : "none")
            .text(formatTwoDecimalValue(residualTotal));

        svg
          .append("rect")
          .attr("class", "residual-hover-hitbox")
          .attr("x", residualX)
          .attr("y", y(Math.max(0, residualTotal)))
          .attr("width", x.bandwidth())
          .attr("height", Math.abs(y(0) - y(residualTotal)))
          .attr("fill", "transparent")
          .on("mouseover", function (event) {
            showResidualBreakdownTooltip(
              event,
              visibleScopeData,
              targetTooltipRef
            );
          })
          .on("mousemove", function (event) {
            moveTooltip(event, targetTooltipRef);
          })
          .on("mouseout", function () {
            hideTooltip(targetTooltipRef);
          });
      }

      svg
        .selectAll(".label")
        .data(regularBarData)
        .enter()
        .append("text")
        .attr("class", "label")
        .attr("x", (d) => (x(d.xKey) ?? 0) + x.bandwidth() / 2 + barOffsetX)
        .attr("y", (d) => y(Math.max(d.start, d.end)) - 6)
        .attr("text-anchor", "middle")
        .style("font-size", exportFitMode && isDense ? "9px" : "10px")
        .style("fill", "#425466")
        .style("cursor", "default")
        .style("display", showLabels ? "block" : "none")
        .text((d) => toSafeNumber(d.value, 0).toLocaleString())
        .on("mouseover", function (event, d) {
          showTooltip(event, d, targetTooltipRef, activeGroupBy);
        })
        .on("mousemove", function (event) {
          moveTooltip(event, targetTooltipRef);
        })
        .on("mouseout", function () {
          hideTooltip(targetTooltipRef);
        });
    };

    useEffect(() => {
      buildChart(svgRef, tooltipRef, false, groupBy, safeMainData);
    }, [data, groupBy, showLabels]);

    useEffect(() => {
      if (!hasManualLabelPreference) {
        setShowLabels(activeDataLength <= DENSE_BAR_THRESHOLD);
      }
    }, [activeDataLength, groupBy, hasManualLabelPreference]);

    useEffect(() => {
      if (isFullscreen) {
        setDialogGroupBy(groupBy);
        setDialogLocalData([]);
        userChangedDialogGroupByRef.current = false;
      }
    }, [isFullscreen, groupBy]);

    useEffect(() => {
      if (!isFullscreen) return;
      if (!userChangedDialogGroupByRef.current) return;
      if (!fetchData) return;

      setDialogLoading(true);

      fetchData(dialogGroupBy)
        .then((result) => {
          setDialogLocalData(Array.isArray(result) ? result : []);
          setDialogLoading(false);
        })
        .catch((err) => {
          console.error("Error loading dialog waterfall data:", err);
          setDialogLocalData([]);
          setDialogLoading(false);
        });
    }, [dialogGroupBy, isFullscreen, fetchData]);

    useEffect(() => {
      if (!isFullscreen) return;

      const source = safeDialogData.length > 0 ? safeDialogData : safeMainData;
      const timer = window.setTimeout(() => {
        buildChart(svgDialogRef, tooltipDialogRef, true, dialogGroupBy, source);
      }, 50);

      return () => window.clearTimeout(timer);
    }, [isFullscreen, data, dialogGroupBy, dialogLocalData, showLabels]);

    useEffect(() => {
      d3.select(svgRef.current)
        .selectAll(".label")
        .style("display", showLabels ? "block" : "none");

      d3.select(svgDialogRef.current)
        .selectAll(".label")
        .style("display", showLabels ? "block" : "none");

      d3.select(svgRef.current)
        .selectAll(
          ".residual-total-label"
        )
        .style("display", showLabels ? "block" : "none");

      d3.select(svgDialogRef.current)
        .selectAll(
          ".residual-total-label"
        )
        .style("display", showLabels ? "block" : "none");
    }, [showLabels]);

    const tooltipStyle: React.CSSProperties = {
      display: "none",
      position: "absolute",
      background: "#fff",
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      padding: "10px 14px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
      pointerEvents: "none",
      zIndex: 10,
      maxWidth: "420px",
      minWidth: "320px",
      width: "max-content",
      whiteSpace: "normal",
      overflow: "hidden",
    };

    const handleDownload = (format: "png" | "jpeg") => {
      if (!svgRef.current) {
        console.warn("SVG reference is missing.");
        return;
      }

      if (!safeMainData.length) {
        console.warn("No valid chart data available for download.");
        return;
      }

      const scopeLabelSelection = d3
        .select(svgRef.current)
        .selectAll(
          ".residual-scope-label-outside, .residual-scope-connector"
        );

      const previousDisplays: string[] = [];
      scopeLabelSelection.each(function () {
        const currentDisplay = d3.select(this).style("display");
        previousDisplays.push(currentDisplay);
      });

      scopeLabelSelection.style("display", showLabels ? "block" : "none");

      downloadChartImage({
        svgRef,
        format,
        fileName: `decarbonization-waterfall-chart.${format}`,
        title: "Decarbonization Waterfall",
        subtitle: "Emissions reductions by measure",
        titlePosition: "bottom",
      });

      scopeLabelSelection.each(function (_, i) {
        d3.select(this).style("display", previousDisplays[i] || "none");
      });
    };

    useImperativeHandle(ref, () => ({
      downloadChart: handleDownload,
      _testSvgRef: svgRef,
    }));

    return (
      <Grid item xs={12} sx={{ mb: 1, width: "100%", maxWidth: "100%" }}>
        <Paper sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
          <Grid item sx={{ m: 1, px: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                width: "100%",
                px: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setHasManualLabelPreference(true);
                    setShowLabels((prev) => !prev);
                  }}
                  data-testid="toggleLabelsButton"
                  sx={{ whiteSpace: "nowrap", textTransform: "none" }}
                >
                  {showLabels ? "Hide Chart Values" : "Show Chart Values"}
                </Button>
                <Tooltip title="View Fullscreen">
                  <IconButton
                    size="small"
                    onClick={() => setIsFullscreen(true)}
                    data-testid="fullscreenToggleButton"
                    sx={{ color: "#425466" }}
                  >
                    <OpenInFullIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Grid>

          <Box
            ref={containerBoxRef}
            sx={{ width: "100%", overflowX: "auto", position: "relative", p: 2 }}
          >
            <div ref={tooltipRef} style={tooltipStyle} />
            <svg ref={svgRef} />
          </Box>
        </Paper>

        <Dialog
          open={isFullscreen}
          onClose={() => setIsFullscreen(false)}
          fullScreen
          PaperProps={{
            sx: { m: 2, borderRadius: 2, height: "calc(100vh - 32px)" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 1.5,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                mr: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "#05004E",
                  lineHeight: 1.2,
                }}
              >
                Decarbonization Waterfall
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#425466",
                  mt: 0.5,
                }}
              >
                Emissions reductions by measure
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setHasManualLabelPreference(true);
                  setShowLabels((prev) => !prev);
                }}
                sx={{ whiteSpace: "nowrap", textTransform: "none" }}
              >
                {showLabels ? "Hide Values" : "Show Values"}
              </Button>
              <Tooltip title="Close">
                <IconButton
                  size="small"
                  onClick={() => setIsFullscreen(false)}
                  sx={{ color: "#425466" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <DialogContent sx={{ p: 2, overflow: "auto", flex: "0 1 auto" }}>
            <Box sx={{ width: "100%", overflowX: "auto", position: "relative" }}>
              {dialogLoading && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "absolute",
                    inset: 0,
                    zIndex: 5,
                    background: "rgba(255,255,255,0.7)",
                  }}
                >
                  <CircularProgress size={48} />
                </Box>
              )}
              <div ref={tooltipDialogRef} style={tooltipStyle} />
              <svg ref={svgDialogRef} />
            </Box>
          </DialogContent>
        </Dialog>
      </Grid>
    );
  }
);

export default MeasureImpactsbyScope;
